import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Grid,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { CreditCard } from "@mui/icons-material";
import { UrlApi } from "../../../../../services/apirest";

const ResumenFactura = ({
  pedido,
  renglones,
  subtotal,
  total,
  totalGeneral,
  onRecalcular,
  onVolverEditar,
  onConfirmarFinal,
}) => {
  const [calculando, setCalculando] = useState(false);
  const [error, setError] = useState(null);
  const [preFactura, setPreFactura] = useState(null);
  const [consumidorFinal, setConsumidorFinal] = useState(0);
  const totalNeto = preFactura?.factura?.ImpTotal || totalGeneral;
  const iva = preFactura?.factura?.ImpIva ?? total;
  const percepciones = preFactura?.factura?.ImpTrib || null;

  const calcularPreFactura = async (cf = consumidorFinal) => {
    setCalculando(true);
    setError(null);

    try {
      const res = await fetch(
        `${UrlApi}/envasado_pedidos/pagoanticipado/${pedido.id}/${cf}`
      );
      const data = await res.json();

      if (!res.ok)
        throw new Error(data?.message || "Error al calcular prefactura");

      setPreFactura(data);
      if (data?.ErrorProcesamiento) setError(data.ErrorProcesamiento);

      return data;
    } catch (err) {
      console.error("Error al calcular pre-factura:", err.message);
      setError(err.message);
      throw err;
    } finally {
      setCalculando(false);
    }
  };

  useEffect(() => {
    if (pedido.situacion_iva === 1 || pedido.situacion_iva === 3) {
      calcularPreFactura();
    }
  }, []);

  const handleReintentar = () => {
    calcularPreFactura(consumidorFinal);
  };

  const FilaPrecio = ({ label, valor, esTotal = false }) => (
    <Box display="flex" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography
        variant={esTotal ? "subtitle1" : "body2"}
        fontWeight={esTotal ? "bold" : "normal"}
      >
        {label}
      </Typography>
      <Typography
        variant={esTotal ? "subtitle1" : "body2"}
        fontWeight={esTotal ? "bold" : "normal"}
        color={esTotal ? "primary" : "inherit"}
      >
        ${valor.toFixed(2)}
      </Typography>
    </Box>
  );

  if (calculando) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={4}
      >
        <CircularProgress size={32} />
        <Typography variant="subtitle1" sx={{ mt: 1.5 }}>
          Calculando factura...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={1.5}
        py={2}
      >
        <Alert severity="error" sx={{ width: "100%", py: 1 }}>
          Error: {error}
        </Alert>
        <Button variant="contained" size="small" onClick={handleReintentar}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Método de pago */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 0.5,
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <CreditCard fontSize="small" color="secondary" />
          <Typography variant="subtitle1" fontWeight="500" color="text.primary">
            Mercado Pago
          </Typography>
        </Box>
      </Paper>

      {/* Cliente */}
      <Paper
        sx={{
          p: 1,
          mb: 1,
          backgroundColor:
            consumidorFinal === 1 ? "#E8F4FD" : "background.paper",
          border: consumidorFinal === 1 ? "2px solid" : "1px solid",
          borderColor: consumidorFinal === 1 ? "primary.main" : "divider",
          transition: "all 0.2s ease",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2" fontWeight="500" color="text.secondary" gutterBottom>
              Cliente
            </Typography>
            <Typography variant="body1">
              {pedido.cliente}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={1} sx={{ mb: 1 }}>
        {/* Productos */}
        <Grid item xs={6}>
          <Paper sx={{ p: 1, minHeight: 140 }}>
            <Typography variant="subtitle2" fontWeight="500" color="text.secondary" gutterBottom>
              Productos ({renglones.length})
            </Typography>
            <Box sx={{ maxHeight: 150, overflow: "auto" }}>
              {renglones.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.75,
                    borderBottom:
                      index < renglones.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" fontWeight="500">
                    {item.cantidad}x
                  </Typography>
                  <Typography variant="body2">{item.nombre}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Desglose de pago */}
        <Grid item xs={6}>
          <Paper sx={{ p: 1, minHeight: 140 }}>
            <Typography variant="subtitle2" fontWeight="500" color="text.secondary" gutterBottom>
              Resumen
            </Typography>

            <Box sx={{ mb: 1 }}>
              <FilaPrecio label="Subtotal:" valor={subtotal} />
              <FilaPrecio label="IVA:" valor={iva} />

              {percepciones !== null && (
                <FilaPrecio
                  label="Perc. IVA:"
                  valor={parseFloat(percepciones)}
                />
              )}

              {preFactura &&
                Object.entries(preFactura).map(
                  ([key, value]) =>
                    typeof value === "number" &&
                    value > 0 &&
                    key !== "percepciones" && (
                      <FilaPrecio key={key} label={`${key}:`} valor={value} />
                    )
                )}
            </Box>

            <Divider sx={{ my: 1 }} />
            <FilaPrecio label="TOTAL:" valor={totalNeto} esTotal />
          </Paper>
        </Grid>
      </Grid>

      {/* Botones de acción */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            onClick={onVolverEditar}
            color="error"
            fullWidth
            size="medium"
          >
            Volver
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="contained"
            onClick={() => onConfirmarFinal(totalNeto)}
            color="primary"
            fullWidth
            size="medium"
            startIcon={<CreditCard />}
            sx={{
              fontWeight: 500,
              textTransform: "none",
            }}
          >
            Generar link de pago
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResumenFactura;